'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { isRadarThemedPath } from '@/lib/radar/theme-routes'

function setThemeCookie(value: string) {
  document.cookie = `theme=${value};path=/;max-age=31536000;samesite=lax;secure`
}

/**
 * Applies the theme rule on every client-side navigation (the layout's
 * THEME_BOOTSTRAP covers the first paint):
 * - marketing routes are always light, whatever is stored;
 * - the Radar app routes (theme-routes.ts) apply the stored choice and sync
 *   it to the cookie the layout reads, so the dashboard's own toggle persists.
 * Without the route check, navigating from a dark dashboard back to the site
 * would leave a half-themed marketing page.
 */
export default function ThemeProvider() {
  const pathname = usePathname()

  useEffect(() => {
    const root = document.documentElement

    // The Radar app surface (the dashboard and, since PIX-207, the owner
    // pilot routes) keeps a light/dark choice; every other route is light.
    if (!isRadarThemedPath(pathname)) {
      root.setAttribute('data-theme', 'light')
      return
    }

    const stored = localStorage.getItem('theme')
    if (stored === 'light' || stored === 'dark') {
      root.setAttribute('data-theme', stored)
      setThemeCookie(stored)
    }

    // Persist toggles made on the dashboard.
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-theme') {
          const current = root.getAttribute('data-theme')
          if (current) setThemeCookie(current)
        }
      }
    })
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })
    return () => observer.disconnect()
  }, [pathname])

  return null
}
